import { useNavigate } from "@tanstack/react-router";
import { ArrowUpRight, Package } from "lucide-react";
import { Button, Card, CardBody, CardHeader, EmptyState, LoadingBlock, PageHeader, Stat, StatusBadge } from "@nahal/ui";
import { useMe, useRequests, useWorkspaces } from "../../lib/queries";
import { api } from "../../lib/api";
import { toast } from "@nahal/ui";

export function HomeScreen() {
  const navigate = useNavigate();
  const me = useMe();
  const workspaces = useWorkspaces();
  const requests = useRequests();

  if (me.isLoading) return <LoadingBlock />;

  const pending = requests.data?.filter((request) => request.status === "PENDING") ?? [];

  async function open(workspaceId: string) {
    try {
      const { token, appUrl } = await api.workspaceToken(workspaceId);
      if (!appUrl) {
        toast.error("That product has no app address configured yet.");
        return;
      }
      window.location.href = `${appUrl}/sso?token=${encodeURIComponent(token)}`;
    } catch {
      toast.error("Could not open that workspace.");
    }
  }

  return (
    <>
      <PageHeader
        title={me.data ? `Hello, ${me.data.account.name.split(" ")[0]}` : "Home"}
        description={me.data?.organization.name}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Products" value={me.data?.products.length ?? 0} />
        <Stat label="Workspaces" value={workspaces.data?.length ?? 0} />
        <Stat label="Open requests" value={pending.length} />
        <Stat label="Members" value="-" hint="See Members" />
      </div>

      <Card className="mt-4">
        <CardHeader title="Your workspaces" />
        {workspaces.isLoading ? (
          <LoadingBlock />
        ) : (workspaces.data?.length ?? 0) === 0 ? (
          <EmptyState
            icon={<Package className="size-5" />}
            title="No workspaces yet"
            description="We set these up for you. Ask us and one will appear here."
            action={
              <Button variant="soft" onClick={() => navigate({ to: "/requests" })}>
                Request a workspace
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-[var(--border-subtle)]">
            {workspaces.data?.map((workspace) => (
              <li key={workspace.id} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-[var(--text-primary)]">
                    {workspace.name}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-[var(--text-secondary)]">{workspace.productCode}</span>
                    <StatusBadge status={workspace.status} />
                  </div>
                </div>
                {workspace.status === "ACTIVE" && (
                  <Button
                    size="sm"
                    variant="soft"
                    icon={<ArrowUpRight className="size-3.5" />}
                    onClick={() => open(workspace.id)}
                  >
                    Open
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {pending.length > 0 && (
        <Card className="mt-4">
          <CardHeader title="Waiting on us" />
          <CardBody className="space-y-2">
            {pending.map((request) => (
              <div key={request.id} className="flex items-center justify-between gap-3">
                <span className="text-sm text-[var(--text-primary)]">
                  {request.kind.toLowerCase()}
                </span>
                <StatusBadge status={request.status} />
              </div>
            ))}
          </CardBody>
        </Card>
      )}
    </>
  );
}
